// Хаусын ерөнхийлөгч (сурагч): өөрийн хаусын сурагчдын өдрийн ирц — өдрөөр, ангиар (зөвхөн харах).
// Ерөнхийлөгчийг админ оноодог; бусдад API 400 "Зөвхөн хаусын ерөнхийлөгч".
import React, { useEffect, useMemo, useState } from "react";
import { Input, Spin } from "antd";
import dayjs from "dayjs";
import { FiChevronLeft, FiChevronRight, FiInbox, FiSearch } from "react-icons/fi";
import { instance } from "utils/axios";
import { ST, ORDER, HAS_TIME, DOW_FULL } from "pages/Home/AttendanceDays";

const NONE = "none";
const LABEL = { [NONE]: "Бүртгэлгүй" };
const chipCls = (s) => ST[s]?.chip || "bg-gray-50 text-gray-500 border-gray-200";
const name = (s) => [s.lastName ? `${s.lastName.charAt(0)}.` : "", s.firstName].filter(Boolean).join(" ");

// Ажлын өдрөөр алхана (Бя, Ня алгасна)
const stepDay = (date, dir) => {
  let d = dayjs(date).add(dir, "day");
  while (d.day() === 0 || d.day() === 6) d = d.add(dir, "day");
  return d.format("YYYY-MM-DD");
};

export default function House() {
  const [date, setDate] = useState(null);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    instance({ method: "get", url: "/student/house", params: date ? { date } : {}, silent: true })
      .then((r) => { setData(r.data); setFailed(false); })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [date]);

  const grades = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.grades
      .map((g) => ({
        ...g,
        students: g.students.filter((s) =>
          (!filter || (s.status || NONE) === filter) &&
          (!q || `${s.firstName} ${s.lastName} ${s.group || ""}`.toLowerCase().includes(q))),
      }))
      .filter((g) => g.students.length);
  }, [data, filter, search]);

  if (failed && !data) {
    return (
      <div className="pt-6 pb-6 max-w-3xl mx-auto">
        <div className="tds-card p-8 flex flex-col items-center gap-2 text-center">
          <FiInbox size={28} className="text-[#6E5E8A]" />
          <div className="font-bold">Мэдээлэл ачаалж чадсангүй</div>
          <div className="text-sm text-[#6E5E8A]">Энэ хэсэг зөвхөн хаусын ерөнхийлөгчид нээлттэй.</div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-16"><Spin /></div>;

  const { house, stats, term } = data;
  const cur = data.date;
  const d = dayjs(cur);
  const isToday = cur === data.today;
  const weekend = d.day() === 0 || d.day() === 6;
  const marked = stats.members - stats.counts[NONE];

  return (
    <div className="pt-4 md:pt-6 pb-8 flex flex-col gap-5 max-w-4xl mx-auto">
      {/* Хаус */}
      <div className="tds-card p-4 md:p-5 flex items-center gap-4">
        <span className="h-16 w-16 rounded-2xl bg-[#FAF7FF] border border-[#E5D8F5] shrink-0 overflow-hidden flex items-center justify-center">
          {house.image ? <img src={house.image} alt={`${house.monName || house.name} хаус`} referrerPolicy="no-referrer" className="h-full w-full object-contain p-1.5" /> : <span className="text-2xl font-extrabold text-[#5C1090]">{(house.monName || house.name || "?").charAt(0)}</span>}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold tracking-[0.08em] uppercase text-[#0E7490]">Хаусын ерөнхийлөгч</div>
          <div className="text-xl md:text-2xl font-extrabold text-[#5C1090] leading-tight truncate">{house.monName || house.name}</div>
          <div className="text-[13px] text-[#6E5E8A]">{stats.members} сурагч</div>
        </div>
        {stats.termPct != null && (
          <div className="text-right shrink-0">
            <div className="text-[28px] md:text-[32px] font-extrabold leading-none text-[#5C1090] tabular-nums">{stats.termPct}%</div>
            <div className="text-xs text-[#6E5E8A] mt-1">{term?.name || "Улирлын"} ирц</div>
          </div>
        )}
      </div>

      {/* Өдөр сонгох */}
      <div className="tds-card p-3 md:p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Өмнөх өдөр" onClick={() => setDate(stepDay(cur, -1))} className="h-10 w-10 rounded-xl border border-[#E5D8F5] inline-flex items-center justify-center text-[#5C1090]">
            <FiChevronLeft size={18} />
          </button>
          <label className="flex-1 min-w-0 relative h-10 rounded-xl border border-[#E5D8F5] flex items-center justify-center gap-2 font-bold cursor-pointer">
            <span className="sm:hidden">{DOW_FULL[d.day()]}, {d.month() + 1}/{d.date()}</span>
            <span className="hidden sm:inline">{DOW_FULL[d.day()]}, {d.month() + 1}-р сарын {d.date()}</span>
            {isToday && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#00BCD4] text-[#0B2530]">Өнөөдөр</span>}
            <input type="date" value={cur} max={data.today} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Огноо сонгох" className="absolute inset-0 opacity-0 cursor-pointer" />
          </label>
          <button type="button" aria-label="Дараах өдөр" disabled={isToday} onClick={() => setDate(stepDay(cur, 1) > data.today ? data.today : stepDay(cur, 1))} className="h-10 w-10 rounded-xl border border-[#E5D8F5] inline-flex items-center justify-center text-[#5C1090] disabled:opacity-30">
            <FiChevronRight size={18} />
          </button>
          {!isToday && (
            <button type="button" onClick={() => setDate(data.today)} className="hidden sm:inline-flex h-10 px-3 rounded-xl border border-[#E5D8F5] items-center text-sm font-bold text-[#5C1090]">Өнөөдөр</button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => setFilter(null)} className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${!filter ? "bg-[#5C1090] text-white border-[#5C1090]" : "bg-white text-[#5C1090] border-[#E5D8F5]"}`}>
            Бүгд {stats.members}
          </button>
          {[...ORDER, NONE].filter((s) => stats.counts[s] > 0).map((s) => (
            <button key={s} type="button" onClick={() => setFilter(filter === s ? null : s)} className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${chipCls(s)} ${filter === s ? "ring-2 ring-offset-1 ring-[#5C1090]" : ""}`}>
              {LABEL[s] || s} {stats.counts[s]}
            </button>
          ))}
        </div>
        {weekend && !marked && <div className="text-sm text-[#6E5E8A]">Амралтын өдөр — ирц бүртгэгдээгүй.</div>}
      </div>

      <Input allowClear prefix={<FiSearch className="text-[#6E5E8A]" />} placeholder="Нэр, бүлгээр хайх" value={search} onChange={(e) => setSearch(e.target.value)} className="!h-10 !rounded-xl" />

      <Spin spinning={loading}>
        <div className="flex flex-col gap-4">
          {grades.map((g) => (
            <section key={g.grade} className="tds-card p-3 md:p-4">
              <div className="flex items-baseline justify-between gap-2 px-1 pb-2">
                <h2 className="text-base font-extrabold text-[#5C1090] m-0">{g.grade}-р анги</h2>
                <span className="text-xs text-[#6E5E8A]">{g.students.length} сурагч{g.termPct != null ? ` · улирал ${g.termPct}%` : ""}</span>
              </div>
              <ul className="flex flex-col divide-y divide-gray-50 m-0 p-0 list-none">
                {g.students.map((s) => (
                  <li key={s.id} className="flex items-center gap-3 px-1 py-2.5">
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] font-semibold truncate">{name(s)}</span>
                      <span className="block text-xs text-[#6E5E8A]">
                        {s.group || "—"}
                        {s.termPct != null && ` · улирал ${s.termPct}%`}
                        {s.absences > 0 && ` · ${s.absences} тасалсан`}
                      </span>
                    </span>
                    {HAS_TIME.includes(s.status) && s.time && <span className="text-xs text-[#6E5E8A] tabular-nums shrink-0">{String(s.time).slice(11, 16)}</span>}
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border shrink-0 ${chipCls(s.status)}`}>{s.status || LABEL[NONE]}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {!grades.length && <div className="tds-card p-6 text-center text-sm text-[#6E5E8A]">Тохирох сурагч алга</div>}
        </div>
      </Spin>
    </div>
  );
}
